import { Fragment, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Bars3Icon, BellIcon, ChevronRightIcon, QuestionMarkCircleIcon, UserCircleIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Link, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import { isSuperAdmin, useAuthStore } from '@/stores/authStore'
import { useCartStore } from '@/stores/cartStore'
import { getAppBreadcrumbs } from '@/lib/dashboardBreadcrumbs'
import { AppLanguageSwitcher } from '@/components/ui/AppLanguageSwitcher'
import { useTranslation } from '@/i18n/useTranslation'
import { translateBreadcrumbLabel } from '@/i18n/breadcrumbLabelMap'
import { useNotificationHistoryStore } from '@/stores/notificationHistoryStore'
import { authService } from '@/services/authService'
import { usePermissionsStore } from '@/stores/permissionsStore'
import { useShopPreferences } from '@/hooks/useShopPreferences'
import {
  rememberCustomerShopSlug,
  customerPostLogoutPath,
} from '@/lib/customerShopReturn'
import { markStaffLogoutIntent } from '@/lib/staffLoginRedirect'

interface HeaderProps {
  onMenuClick: () => void
  /** When true on desktop, show the menu button (sidebar is collapsed). */
  desktopCollapsed?: boolean
  /** Customer portal: shop brand lives in this header bar. */
  isCustomer?: boolean
}

const iconButtonClass =
  'inline-flex h-11 w-11 items-center justify-center rounded-full bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'

const Header: React.FC<HeaderProps> = ({
  onMenuClick,
  desktopCollapsed = false,
  isCustomer = false,
}) => {
  const { t } = useTranslation('common')
  const { t: tDash } = useTranslation('dashboard')
  const { user, performLogout, applySession } = useAuthStore()
  const clearPermissions = usePermissionsStore((s) => s.clear)
  const { shop } = useShopPreferences()
  const { total_items } = useCartStore()
  const location = useLocation()
  const crumbs = getAppBreadcrumbs(location.pathname)
  const userName = typeof user?.name === 'string' ? user.name.trim() : ''
  const userInitial = userName ? userName.charAt(0).toUpperCase() : ''
  const [profileOpen, setProfileOpen] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [clearingActing, setClearingActing] = useState(false)
  const profileRef = useRef<HTMLDivElement | null>(null)
  const notificationsRef = useRef<HTMLDivElement | null>(null)
  const notificationItems = useNotificationHistoryStore((s) => s.items)
  const markAllRead = useNotificationHistoryStore((s) => s.markAllRead)
  const clearAllNotifications = useNotificationHistoryStore((s) => s.clearAll)
  const unreadCount = notificationItems.filter((n) => !n.read).length
  // Home shop for platform admin is the platform shop; banner only when acting as a tenant.
  const showActingBanner =
    isSuperAdmin() && Boolean(user?.shop_id) && shop != null && !shop.is_platform_shop
  const shopContextName =
    (shop?.name && String(shop.name).trim()) ||
    (user?.shop_name && String(user.shop_name).trim()) ||
    (user?.shop_code && String(user.shop_code).trim()) ||
    (user?.shop_id != null ? `#${user.shop_id}` : '')
  // Tenant staff (ShopQuay login → their company): show which shop they are in.
  const showShopContextChip =
    !showActingBanner &&
    !user?.is_web_user &&
    Boolean(user?.shop_id) &&
    Boolean(shopContextName) &&
    !shopContextName.startsWith('#')

  const shopPrimary = useMemo(() => {
    if (!isCustomer) return null
    const raw = String(shop?.primary_color || '').trim()
    return raw || '#4f46e5'
  }, [isCustomer, shop?.primary_color])

  const shopStorefrontHref = useMemo(() => {
    if (!isCustomer) return null
    const slug = String(shop?.website_slug || '').trim()
    return slug ? `/shop/${encodeURIComponent(slug)}` : null
  }, [isCustomer, shop?.website_slug])

  const shopDisplayName = useMemo(() => {
    if (!isCustomer) return null
    return String(shop?.name || '').trim() || null
  }, [isCustomer, shop?.name])

  const shopLogoUrl = isCustomer ? shop?.logo_url || null : null

  useEffect(() => {
    if (!isCustomer) return
    rememberCustomerShopSlug(shop?.website_slug)
  }, [isCustomer, shop?.website_slug])

  const handleLogout = () => {
    // Capture destination before auth clears — otherwise ProtectedRoute races to
    // bare /auth/customer/login (platform page with no shop).
    const dest = isCustomer
      ? shopStorefrontHref || customerPostLogoutPath()
      : '/auth/staff/login'

    if (!isCustomer) {
      markStaffLogoutIntent()
    }

    void performLogout().finally(() => {
      window.location.assign(dest)
    })
  }

  const handleClearActing = async () => {
    try {
      setClearingActing(true)
      const response = await authService.clearActingShop()
      if (response.success && response.data?.token && response.data.user) {
        clearPermissions()
        applySession({
          token: response.data.token,
          user: response.data.user,
          expires_in: response.data.expires_in,
          idle_timeout_minutes: response.data.idle_timeout_minutes,
          max_session_expires_at: response.data.max_session_expires_at,
        })
        toast.success(response.message || 'Cleared shop acting context')
      } else {
        toast.error(response.message || 'Failed to clear acting context')
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      toast.error(err?.response?.data?.message || 'Failed to clear acting context')
    } finally {
      setClearingActing(false)
    }
  }

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!profileRef.current) return
      if (!profileRef.current.contains(event.target as Node)) {
        setProfileOpen(false)
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setNotificationsOpen(false)
      }
    }
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setProfileOpen(false)
        setNotificationsOpen(false)
      }
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onEscape)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onEscape)
    }
  }, [])

  useEffect(() => {
    if (!notificationsOpen) return
    markAllRead()
  }, [notificationsOpen, markAllRead])

  const brandLabel = shopDisplayName || t('appName')
  const brandInner = (
    <>
      {shopLogoUrl ? (
        <img
          src={shopLogoUrl}
          alt=""
          className="h-9 w-auto max-h-9 max-w-[3rem] shrink-0 object-contain object-left sm:h-10 sm:max-h-10 sm:max-w-[3.5rem]"
        />
      ) : null}
      <span
        className="min-w-0 truncate text-base font-semibold tracking-tight sm:text-lg"
        style={
          shopPrimary
            ? {
                color: shopPrimary,
                fontFamily: shop?.font_family || undefined,
              }
            : undefined
        }
      >
        {brandLabel}
      </span>
    </>
  )

  const customerBrand = isCustomer ? (
    shopStorefrontHref ? (
      <Link
        to={shopStorefrontHref}
        className="flex min-w-0 max-w-[min(100%,18rem)] items-center gap-2.5 rounded-md sm:max-w-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
        style={
          shopPrimary
            ? ({ ['--tw-ring-color' as string]: shopPrimary } as CSSProperties)
            : undefined
        }
        title={t('sidebar.goToShopStorefront', { name: brandLabel })}
        aria-label={t('sidebar.goToShopStorefront', { name: brandLabel })}
      >
        {brandInner}
      </Link>
    ) : (
      <div className="flex min-w-0 max-w-[min(100%,18rem)] items-center gap-2.5 sm:max-w-xs">
        {brandInner}
      </div>
    )
  ) : null

  return (
    <header
      className={[
        'relative z-30 flex-shrink-0 flex min-h-14 bg-white shadow pt-[env(safe-area-inset-top)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]',
        isCustomer ? 'border-b border-gray-200/80' : '',
      ].join(' ')}
    >
      <button
        type="button"
        className={[
          'px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500',
          'inline-flex min-h-14 min-w-14 items-center justify-center',
          desktopCollapsed ? '' : 'md:hidden',
        ].join(' ')}
        onClick={onMenuClick}
        title={desktopCollapsed ? t('header.openSidebar') : undefined}
      >
        <span className="sr-only">{t('header.openSidebar')}</span>
        <Bars3Icon className="h-6 w-6" aria-hidden="true" />
      </button>

      <div className="flex-1 px-4 flex justify-between items-center min-w-0 gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-5">
          {customerBrand}

          {!isCustomer ? (
            <>
              <nav aria-label={t('breadcrumb')} className="hidden sm:flex min-w-0 items-center text-sm">
                <ol className="flex flex-wrap items-center gap-x-1 gap-y-0.5 text-gray-500">
                  {crumbs.map((crumb, i) => {
                    const label = translateBreadcrumbLabel(crumb.label, tDash)
                    return (
                      <Fragment key={`${crumb.label}-${i}`}>
                        {i > 0 && (
                          <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
                        )}
                        <li className="min-w-0">
                          {crumb.to && i < crumbs.length - 1 ? (
                            <Link
                              to={crumb.to}
                              className="text-primary-600 hover:text-primary-800 font-medium truncate"
                            >
                              {label}
                            </Link>
                          ) : (
                            <span className="font-medium text-gray-900 truncate">{label}</span>
                          )}
                        </li>
                      </Fragment>
                    )
                  })}
                </ol>
              </nav>
              <div className="sm:hidden min-w-0 flex-1 pr-2">
                {crumbs.length > 0 && (
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {translateBreadcrumbLabel(crumbs[crumbs.length - 1].label, tDash)}
                  </p>
                )}
              </div>
            </>
          ) : (
            <nav
              aria-label={t('breadcrumb')}
              className="hidden min-w-0 items-center text-sm md:flex border-l border-gray-200 pl-4 sm:pl-5"
            >
              <ol className="flex flex-wrap items-center gap-x-1 gap-y-0.5 text-gray-500">
                {crumbs.map((crumb, i) => {
                  const label = translateBreadcrumbLabel(crumb.label, tDash)
                  return (
                    <Fragment key={`${crumb.label}-${i}`}>
                      {i > 0 && (
                        <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
                      )}
                      <li className="min-w-0">
                        {crumb.to && i < crumbs.length - 1 ? (
                          <Link
                            to={crumb.to}
                            className="text-primary-600 hover:text-primary-800 font-medium truncate"
                          >
                            {label}
                          </Link>
                        ) : (
                          <span className="font-medium text-gray-900 truncate">{label}</span>
                        )}
                      </li>
                    </Fragment>
                  )
                })}
              </ol>
            </nav>
          )}
        </div>

        <div className="flex items-center shrink-0">
          {/* Cart icon for customers */}
          {user?.is_web_user && (
            <div className="relative mr-1">
              <button type="button" className={iconButtonClass}>
                <span className="sr-only">{t('header.viewCart')}</span>
                <div className="relative">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.5 6M7 13l-1.5 6m0 0h9"
                    />
                  </svg>
                  {total_items > 0 && (
                    <span className="absolute -top-2 -right-2 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {total_items}
                    </span>
                  )}
                </div>
              </button>
            </div>
          )}

          {/* Notifications */}
          <div className="relative mr-1" ref={notificationsRef}>
            <button
              type="button"
              onClick={() => setNotificationsOpen((v) => !v)}
              className={`${iconButtonClass} relative`}
            >
              <span className="sr-only">{t('header.viewNotifications')}</span>
              <BellIcon className="h-6 w-6" aria-hidden="true" />
              {unreadCount > 0 && (
                <span className="absolute top-1.5 right-1.5 h-4 min-w-4 px-1 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {notificationsOpen && (
              <div className="origin-top-right absolute right-0 mt-2 w-96 max-w-[90vw] rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-900">{t('header.viewNotifications')}</p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="text-xs text-gray-500 hover:text-gray-700 min-h-8 px-2"
                      onClick={markAllRead}
                    >
                      Mark all read
                    </button>
                    <button
                      type="button"
                      className="text-xs text-gray-500 hover:text-gray-700 min-h-8 px-2"
                      onClick={clearAllNotifications}
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notificationItems.length === 0 ? (
                    <p className="px-4 py-6 text-sm text-gray-500">No recent notifications.</p>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {notificationItems.map((item) => (
                        <li key={item.id} className={`px-4 py-3 ${item.read ? 'bg-white' : 'bg-blue-50/40'}`}>
                          <div className="flex items-start justify-between gap-3">
                            <p className="text-sm text-gray-800 break-words">{item.message}</p>
                            <span
                              className={`mt-0.5 inline-block h-2.5 w-2.5 rounded-full ${
                                item.type === 'error'
                                  ? 'bg-red-500'
                                  : item.type === 'success'
                                    ? 'bg-emerald-500'
                                    : 'bg-gray-400'
                              }`}
                            />
                          </div>
                          <p className="mt-1 text-[11px] text-gray-500">
                            {new Date(item.createdAt).toLocaleTimeString()}
                          </p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>

          {showActingBanner ? (
            <div className="hidden sm:flex items-center gap-2 mr-3 max-w-xs">
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-800 border border-indigo-100 truncate"
                title={shopContextName || `Shop #${user?.shop_id}`}
              >
                {t('header.actingAs', {
                  name: shopContextName || `#${user?.shop_id}`,
                })}
              </span>
              <button
                type="button"
                onClick={() => void handleClearActing()}
                disabled={clearingActing}
                className="inline-flex items-center justify-center h-9 w-9 rounded-full text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
                title={t('header.stopActing')}
                aria-label={t('header.stopActing')}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          ) : showShopContextChip ? (
            <div className="hidden sm:flex items-center mr-3 max-w-xs">
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-800 border border-sky-100 truncate"
                title={t('header.workingInTitle', { name: shopContextName })}
              >
                {t('header.workingIn', { name: shopContextName })}
              </span>
            </div>
          ) : null}

          <AppLanguageSwitcher variant="compact" className="hidden sm:inline-flex mr-2" />

          {!user?.is_web_user ? (
            <a
              href="/help"
              target="_blank"
              rel="noopener noreferrer"
              className={`mr-1 ${iconButtonClass}`}
              title={t('header.helpCenter')}
              aria-label={t('header.helpCenter')}
            >
              <QuestionMarkCircleIcon className="h-6 w-6" aria-hidden="true" />
            </a>
          ) : null}

          {/* Profile dropdown */}
          <div className="ml-1 relative" ref={profileRef}>
            <button
              type="button"
              onClick={() => setProfileOpen((v) => !v)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              aria-haspopup="menu"
              aria-expanded={profileOpen}
              aria-label={t('header.openUserMenu')}
            >
              <div className="h-8 w-8 rounded-full bg-gray-300 border border-gray-400/40 flex items-center justify-center">
                {userInitial ? (
                  <span className="text-sm font-medium text-gray-700">{userInitial}</span>
                ) : (
                  <UserCircleIcon className="h-5 w-5 text-gray-600" aria-hidden="true" />
                )}
              </div>
            </button>

            {profileOpen && (
              <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-200">
                  <p className="font-medium break-words">{user?.name || t('header.user')}</p>
                  <p className="text-gray-500 break-words">{user?.email || ''}</p>
                </div>
                <Link
                  to={user?.is_web_user ? '/customer/profile' : '/dashboard/profile'}
                  className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => setProfileOpen(false)}
                >
                  {t('header.yourProfile')}
                </Link>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100"
                >
                  {t('header.signOut')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header
